import React, { useEffect, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';

import { Image } from '../../../api/caseApiTypes';
import {getArticlePhotoshopData} from "../../../api/articleStoreApi";
import {ArticlePhotoshopData} from "../../../api/articleStoreApiTypes";

interface Props {
  image: Image;
}

const DataRow: React.FC<{
  label: string;
  multiline?: boolean;
  children?: React.ReactNode
}> = ({ label, multiline = false, children }) => {
  const text = multiline
   ? Array.isArray(children)
      ? children.reduce<React.ReactNode[]>((acc, c, i) => i === 0 ? [c] : [...acc, <br key={i} />, c], [])
      : String(children)
  : Array.isArray(children)
      ? children.map(c => String(c ?? '')).join('')
      : String(children ?? '');

  const copyText = Array.isArray(children)
    ? children.map(c => String(c ?? '')).join(multiline ? '\n' : '')
    : String(children ?? '');

  const firstWord = copyText.split(' ')[0].split('%')[0];
  return (
    <tr>
      <td>{label}:</td>
      <td>{text}</td>
      <td>
        <ContentCopyIcon onClick={() => navigator.clipboard.writeText(copyText)} />
      </td>
      {firstWord != text && !multiline && <td>
        <ContentCopyIcon onClick={() => navigator.clipboard.writeText(firstWord)} color="secondary" />
      </td>}
    </tr>
  );
};

const DateRow: React.FC<{
  label: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
}> = ({ label, value, onChange }) => {
  return (
    <tr>
      <td>{label}:</td>
      <td colSpan={3}>
        <DatePicker
          label={label}
          value={value}
          onChange={onChange}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
      </td>
    </tr>
  );
};

export const ImagesPhotoshopInformationView: React.FC<Props> = ({
  image: {
    productName,
    additionalArticles,
  },
}) => {
  const [photoshopDataList, setPhotoshopDataList] = useState<ArticlePhotoshopData[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  useEffect(() => {
    if (!additionalArticles || additionalArticles.length === 0) {
      setPhotoshopDataList([]);
      return;
    }

    Promise.all(additionalArticles.map(article =>
      getArticlePhotoshopData(article.articleId).catch(() => null)
    ))
      .then((results) => {
        setPhotoshopDataList(results.filter((pd): pd is ArticlePhotoshopData => pd !== null));
      })
  }, [additionalArticles]);

  const hasMoreThanOnePrice = photoshopDataList.some(pd => pd.prices && pd.prices.length >= 1);

  return (
    <table>
      <tbody>
        {hasMoreThanOnePrice && (
          <DateRow
            label="Giltig"
            value={selectedDate}
            onChange={setSelectedDate}
          />
        )}

        {photoshopDataList.map((pd, index) => {
          const currentPrice = pd.prices?.find(price => {
            if (!selectedDate) return false;
            const from = new Date(price.from);
            const to = new Date(price.to);
            const target = new Date(selectedDate);
            return target >= from && target <= to;
          });

          if (!currentPrice) return null;

          // Convert the prices to have period as thousand separator.
          // It makes it easier for the users to copy-paste the prices.
          return (
            <DataRow key={index} label="Information" multiline={true}>
              {pd.productTypeDisplay}
              {pd.name}
              {currentPrice.salesPrice.toLocaleString("de-DE")}
              {currentPrice.originalPrice.toLocaleString("de-DE")}
            </DataRow>
          );
        })}
      </tbody>
    </table>
  );
};
